import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useExperience } from '../../contexts/ExperienceContext';
interface SessionMemoryProps {
  open: boolean;
  onClose: () => void;
}

/** Makes the conversation visible: what this page has understood so far. */
export function SessionMemory({
  open,
  onClose
}: SessionMemoryProps) {
  const {
    memory,
    accent,
    emphasis
  } = useExperience();
  return <AnimatePresence data-id="element-34" data-mp="1lpzaz9zdzo76">
      {open && <>
          <motion.button type="button" aria-label="Close session panel" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-40 bg-ink/70 backdrop-blur-[2px]" data-id="element-35" data-mp="1hqyu3x0gyvmt" />
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[26rem] flex-col border-l border-enamel/10 bg-ink-2/95 backdrop-blur-xl" data-id="element-36" data-mp="1cqhov9ey2ow">
            <header className="flex items-start justify-between border-b border-enamel/10 p-6" data-id="element-37" data-mp="za58fvkyebtm">
              <div data-id="element-38" data-mp="140twt21c6soj">
                <p className="label-mono text-[10px] text-mute" data-id="element-39" data-mp="141di8sq73z7b">This conversation</p>
                <p className="mt-2 text-xl font-light text-enamel" data-id="element-40" data-mp="1906vql5hfq7h">
                  What we&apos;ve understood so far
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close session panel" className="rounded-full border border-enamel/15 p-2 text-mute transition-colors hover:border-enamel/40 hover:text-enamel" data-id="element-41" data-mp="1dx6ppxp252i8">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-42" data-mp="3kr250iavnbah" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-6" data-id="element-43" data-mp="3orkw2a4y4dbm">
              {memory.length === 0 ? <p className="text-sm font-light leading-relaxed text-mute" data-id="element-44" data-mp="vx47hagd44gi">
                  Nothing yet. As you make choices, this panel shows what the page is carrying
                  forward — so later content stays relevant to your cases.
                </p> : <ul className="space-y-5" data-id="element-45" data-mp="10uw3ogo1icqi">
                  {memory.map(entry => <motion.li key={entry.label} initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1]
            }} className="border-l pl-4" style={{
              borderColor: accent
            }} data-id="element-46" data-mp="1eueyy8vr5tle">
                      <p className="label-mono text-[9px] text-mute/70" data-id="element-47" data-mp="1xleopcb2n7fq">{entry.label}</p>
                      <p className="mt-1.5 text-base font-light leading-snug text-enamel" data-id="element-48" data-mp="1sn4sqzhiw2t2">
                        {entry.value}
                      </p>
                    </motion.li>)}
                </ul>}
            </div>

            <footer className="border-t border-enamel/10 p-6" data-id="element-49" data-mp="2h6yfz5l9qjid">
              <p className="text-xs font-light leading-relaxed text-mute" data-id="element-50" data-mp="236x7b3yri7sc">
                {memory.length > 1 ? <>
                    The rest of the experience keeps emphasising{' '}
                    <span className="text-enamel" data-id="element-51" data-mp="1thdn9uzbc7vw">{emphasis}</span>.
                  </> : 'Your selections stay in this session only.'}
              </p>
            </footer>
          </motion.aside>
        </>}
    </AnimatePresence>;
}